// src/components/TrialExpiredModal.jsx
// Modal shown when trial has expired and user tries to perform an action
// Encourages upgrade by showing what they've built during trial

import React from 'react';
import { useNavigate } from 'react-router-dom';

const TrialExpiredModal = ({ 
  isOpen, 
  onClose,
  clubStats = {} // Pass in stats like pitchCount, teamCount, allocationCount
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    navigate('/pricing'); // Navigate to pricing page instead of settings
    onClose();
  };

  const handleContinueBrowsing = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate('/menu'); // Navigate to menu instead of just closing
    onClose();
  };

  const {
    pitchCount = 0,
    teamCount = 0,
    allocationCount = 0
  } = clubStats;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '560px',
          width: '100%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#6b7280',
            padding: '4px 8px',
            lineHeight: 1
          }}
        >
          ×
        </button>

        {/* Icon */}
        <div style={{
          margin: '0 auto 24px',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <img 
            src={require('../assets/images/gem.gif')}
            alt="Premium" 
            style={{
              width: '80px',
              height: '80px'
            }}
          />
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#111827',
          marginBottom: '16px',
          textAlign: 'center',
          lineHeight: '1.2'
        }}>
          Your 7-Day Trial Has Ended
        </h2>

        {/* Description */}
        <p style={{
          fontSize: '16px',
          color: '#6b7280',
          marginBottom: '24px',
          textAlign: 'center',
          lineHeight: '1.6'
        }}>
          You can still view your data, but you'll need to upgrade to continue creating and editing allocations.
        </p>

        {/* Stats - What they've built */}
        {(pitchCount > 0 || teamCount > 0 || allocationCount > 0) && (
          <div style={{
            backgroundColor: '#f9fafb',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '28px'
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '12px',
              textAlign: 'center'
            }}>
              You've built a lot during your trial:
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-around',
              gap: '16px'
            }}>
              {pitchCount > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: '700',
                    color: '#243665',
                    marginBottom: '4px'
                  }}>
                    {pitchCount}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#6b7280'
                  }}>
                    {pitchCount === 1 ? 'Pitch' : 'Pitches'}
                  </div>
                </div>
              )}
              {teamCount > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: '700',
                    color: '#243665',
                    marginBottom: '4px'
                  }}>
                    {teamCount}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#6b7280'
                  }}>
                    {teamCount === 1 ? 'Team' : 'Teams'}
                  </div>
                </div>
              )}
              {allocationCount > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: '700',
                    color: '#243665',
                    marginBottom: '4px'
                  }}>
                    {allocationCount}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#6b7280'
                  }}>
                    {allocationCount === 1 ? 'Allocation' : 'Allocations'}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Benefits */}
        <div style={{ marginBottom: '28px' }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '12px'
          }}>
            Upgrade to continue:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
              'Create and edit allocations',
              'Manage teams and pitches',
              'Share schedules with parents & coaches',
              'Access reports and analytics',
              'Get priority support'
            ].map((benefit, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '14px',
                  color: '#374151'
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  flexShrink: 0
                }}>
                  ✓
                </div>
                {benefit}
              </div>
            ))}
          </div>
        </div>

        {/* Upgrade prompt */}
        <div style={{
          fontSize: '14px',
          color: '#6b7280',
          textAlign: 'center',
          marginBottom: '24px',
          padding: '12px',
          backgroundColor: '#f0f9ff',
          borderRadius: '8px'
        }}>
          Click <strong style={{ color: '#243665' }}>Upgrade Now</strong> to see a plan that suits your club
        </div>

        {/* Action buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '12px'
        }}>
          <button
            onClick={handleUpgrade}
            style={{
              flex: 1,
              padding: '14px 24px',
              backgroundColor: '#243665',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#1e2f54';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#243665';
            }}
          >
            Upgrade Now
          </button>
        </div>

        {/* Cancel option */}
        <button
          onClick={handleContinueBrowsing}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: 'transparent',
            color: '#6b7280',
            border: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.color = '#374151';
          }}
          onMouseLeave={(e) => {
            e.target.style.color = '#6b7280';
          }}
        >
          Continue Browsing (Read-Only)
        </button>
      </div>
    </div>
  );
};

export default TrialExpiredModal;
